import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { brand, nav } from "../content";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 50, damping: 20 }}
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${
        scrolled ? "bg-bone/85 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between py-5 md:py-6">
        <a
          href="#top"
          data-testid="brand-logo"
          className="group flex items-baseline gap-2"
        >
          <span className="font-serif text-xl md:text-2xl tracking-tight text-charcoal">
            {brand.short}
          </span>
          <span className="hidden md:inline text-[10px] uppercase tracking-[0.3em] text-taupe">
            Studio
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10" data-testid="primary-nav">
          {nav.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-sm text-charcoal/80 hover:text-charcoal transition-colors duration-300"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid="book-consultation-btn"
            className="hidden md:inline-flex items-center gap-2 bg-charcoal text-bone px-5 py-3 text-sm tracking-wide hover:bg-charcoal/85 transition-colors duration-300 group"
          >
            Book Consultation
            <ArrowUpRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 text-charcoal"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-bone border-t border-charcoal/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {nav.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="text-charcoal text-base"
                >
                  {n.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-book-consultation-btn"
                className="inline-flex items-center justify-between bg-charcoal text-bone px-5 py-3 text-sm"
              >
                Book Consultation <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
